import React, { FC } from 'react';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import ReactHtmlParser from 'react-html-parser';
import { ExamQuestion as ExamQuestionShape } from './examState';

/** An option or a question body, which may be plain text, rich text or an image. */
const Content: FC<{ text?: string; contentType?: string; image?: string }> = ({
  text,
  contentType,
  image,
}) => (
  <Box>
    {!!text && contentType === 'richText' ? (
      <Box sx={{ '& p': { margin: 0 } }}>{ReactHtmlParser(text)}</Box>
    ) : (
      !!text && <Typography variant='body1' component='span'>{text}</Typography>
    )}
    {!!image && (
      <Box
        component='img'
        src={image}
        alt=''
        sx={{ display: 'block', maxWidth: '100%', mt: text ? 1 : 0, borderRadius: 1 }}
      />
    )}
  </Box>
);

const LETTERS = 'ABCDEFGHIJ'.split('');

/**
 * One question of a mock paper, as a student sits it.
 *
 * Deliberately unlike the practice player: nothing is marked, nothing is
 * revealed, and the answer stays changeable until the paper is submitted. The
 * server sends both multiple choice types as one list of options, each carrying
 * the value its choice sends back, so this does not need to know which type it
 * is drawing.
 */
export const ExamQuestion: FC<{
  question: ExamQuestionShape;
  value: string[];
  disabled?: boolean;
  onChange: (value: string[]) => void;
}> = ({ question, value, disabled, onChange }) => {
  const body = question?.question ?? {};
  const options: any[] = body.answer ?? [];
  const selected = value?.[0] ?? '';

  return (
    <Box>
      {!!body.questionHead && (
        <Box sx={{ mb: 2, color: 'text.secondary' }}>
          <Content text={body.questionHead} contentType={body.questionContentType} />
        </Box>
      )}

      <Box sx={{ mb: 3, fontSize: '1.05rem' }}>
        <Content
          text={body.question}
          contentType={body.questionContentType}
          image={body.questionImage}
        />
      </Box>

      {options.length === 0 ? (
        <Typography variant='body2' color='error'>
          This question has no options to choose from. Please tell your teacher.
        </Typography>
      ) : (
        <RadioGroup
          value={selected}
          // An answer can be changed as often as the student likes, and clearing
          // it is what leaves the question unanswered.
          onChange={(event) => onChange([event.target.value])}>
          {options.map((option, index) => {
            const optionValue = `${option?.value ?? index}`;
            const isSelected = optionValue === selected;

            return (
              <FormControlLabel
                key={optionValue}
                value={optionValue}
                disabled={disabled}
                sx={{
                  alignItems: 'flex-start',
                  m: 0,
                  mb: 1,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isSelected ? '#0F5C55' : '#D3DAD5',
                  background: isSelected ? '#EAF2F0' : 'transparent',
                  '&:hover': { borderColor: '#0F5C55' },
                }}
                control={<Radio sx={{ pt: 0.25 }} />}
                label={
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Typography
                      variant='body1'
                      sx={{ fontWeight: 600, minWidth: '1.2rem', color: 'text.secondary' }}>
                      {LETTERS[index] ?? index + 1}
                    </Typography>
                    <Content
                      text={option?.answer}
                      contentType={option?.answerContentType}
                      image={option?.answerImage}
                    />
                  </Box>
                }
              />
            );
          })}
        </RadioGroup>
      )}

      {selected !== '' && !disabled && (
        <Box sx={{ mt: 1 }}>
          <Typography
            component='button'
            type='button'
            variant='caption'
            onClick={() => onChange([])}
            sx={{
              background: 'none',
              border: 'none',
              p: 0,
              cursor: 'pointer',
              color: 'text.secondary',
              textDecoration: 'underline',
            }}>
            Clear my answer
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ExamQuestion;
